import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Palette, RotateCcw, X, Check, Copy, Send, Upload } from 'lucide-react';
import { IS_DEMO, getPersona, withPersonaLook } from '@/lib/demo';
import { SKINS, BACKGROUNDS, CONSTELLATIONS, resolveAppearance, type SkinId, type BackgroundId, type ConstellationId } from '@/lib/skins';
import {
  FONTS, PRESETS, RADIUS_SCALE, WISHLIST_PROVIDERS,
  useDemoCustomization, setCustomization, resetCustomization, resetLook, setContent, replaceCustomization, parseCustomization, isCustomized, hexIsLight,
  type DemoCustomization, type RadiusId, type FontId, type WishlistProvider,
} from '@/lib/customizer';
import { SOCIAL_PLATFORMS, socialHandle } from '@/lib/socials';
import { buildPlans, planLabel } from '@/lib/plans';
import { useTelegram } from '@/hooks/useTelegram';
import { useAppConfig } from '@/hooks/useAppConfig';
import { trackDemoDebounced, sendDemoEvent, canSendToTeam } from '@/lib/demoTracking';

/**
 * Demo-only "Personaliza" sheet, applied live to the whole app and kept only
 * in this tab: skin, animated background and constellation, colours, corner
 * radius and type, plus the creator's own content (name, bio, prices, social
 * networks, wishlist). "Exportar" copies everything as JSON (or sends it to
 * the team through the demo bot) so the real app can be built from it.
 * "Restablecer" returns to the build's demo. Renders nothing outside the demo build.
 */

type Tab = 'look' | 'bg' | 'color' | 'shape' | 'type' | 'profile' | 'socials' | 'export';

const COPY = {
  es: {
    open: 'Personalizar la demo', title: 'Personaliza', subtitle: 'Solo lo ves tú · se borra al cerrar',
    tabs: { look: 'Estilo', bg: 'Fondo', color: 'Color', shape: 'Forma', type: 'Letra', profile: 'Perfil', socials: 'Redes', export: 'Exportar' } as Record<Tab, string>,
    presets: 'Looks rápidos', skins: 'Skin base', bgs: 'Fondo animado', bgColors: 'Colores del fondo', motion: 'Movimiento', density: 'Cantidad',
    stars: 'Constelación', interactive: 'Reacciona al toque', interactiveHint: 'El fondo se deforma y brilla donde tocas; un toque lanza una onda.',
    name: 'Nombre artístico', bio: 'Bio corta', prices: 'Precios (COP)', priceFirst: 'Primer día', priceMonthly: 'Mes normal',
    plansView: 'Ver planes como', planAuto: 'Automático', planFirst: 'Primer día', planLater: 'Fan que vuelve',
    plansHint: 'El primer día el fan solo ve la oferta de bienvenida. Desde el segundo día ve las promos:',
    networks: 'Tus redes', networksHint: 'Escribe tu @usuario o pega el enlace. Las que dejes vacías no aparecen.',
    wishlist: 'Lista de deseos', wishlistOn: 'Mostrar wishlist', wishlistUrl: 'Enlace de tu lista',
    exportTitle: 'Tu configuración', exportHint: 'Copia esto y envíaselo al equipo: con eso armamos tu app tal cual la dejaste aquí.',
    copy: 'Copiar configuración', copied: 'Copiada', send: 'Enviar al equipo', sent: 'Enviada al equipo', sendFail: 'No se pudo enviar, cópiala',
    importTitle: 'Pegar una configuración', importBtn: 'Aplicar', importBad: 'Ese texto no es una configuración válida',
    brand: 'Color de marca', accent: 'Acento', text: 'Texto', ground: 'Fondo de pantalla', palettes: 'Paletas',
    radius: 'Esquinas', radiusNames: { square: 'Cuadrado', soft: 'Suave', round: 'Redondo', pill: 'Muy redondo' } as Record<RadiusId, string>,
    display: 'Títulos', body: 'Texto', size: 'Tamaño', reset: 'Restablecer', done: 'Listo',
    note: 'Después de tu primer mes gratis, podemos configurar tu panel completamente.',
    still: 'Fijo', slow: 'Lento', fast: 'Rápido',
  },
  en: {
    open: 'Customize the demo', title: 'Customize', subtitle: 'Only you see it · cleared on close',
    tabs: { look: 'Look', bg: 'Background', color: 'Color', shape: 'Shape', type: 'Type', profile: 'Profile', socials: 'Networks', export: 'Export' } as Record<Tab, string>,
    presets: 'Quick looks', skins: 'Base skin', bgs: 'Animated background', bgColors: 'Background colours', motion: 'Motion', density: 'Density',
    stars: 'Constellation', interactive: 'React to touch', interactiveHint: 'The background bends and glows where you touch; a tap sends out a ripple.',
    name: 'Stage name', bio: 'Short bio', prices: 'Prices (COP)', priceFirst: 'First day', priceMonthly: 'Regular month',
    plansView: 'Show plans as', planAuto: 'Automatic', planFirst: 'First day', planLater: 'Returning fan',
    plansHint: 'On the first day the fan only sees the welcome offer. From the second day on, the promos:',
    networks: 'Your networks', networksHint: 'Type your @handle or paste the link. Empty ones are hidden.',
    wishlist: 'Wishlist', wishlistOn: 'Show wishlist', wishlistUrl: 'Link to your list',
    exportTitle: 'Your configuration', exportHint: 'Copy this and send it to the team: we build your app exactly as you left it here.',
    copy: 'Copy configuration', copied: 'Copied', send: 'Send to the team', sent: 'Sent to the team', sendFail: 'Could not send, copy it instead',
    importTitle: 'Paste a configuration', importBtn: 'Apply', importBad: 'That text is not a valid configuration',
    brand: 'Brand colour', accent: 'Accent', text: 'Text', ground: 'Page background', palettes: 'Palettes',
    radius: 'Corners', radiusNames: { square: 'Square', soft: 'Soft', round: 'Round', pill: 'Very round' } as Record<RadiusId, string>,
    display: 'Headings', body: 'Body', size: 'Size', reset: 'Reset', done: 'Done',
    note: 'After your first free month, we can fully set up your panel.',
    still: 'Still', slow: 'Slow', fast: 'Fast',
  },
};

const BG_LABELS: Record<BackgroundId, { es: string; en: string }> = {
  nebula: { es: 'Nebulosa', en: 'Nebula' }, aurora: { es: 'Aurora', en: 'Aurora' }, liquid: { es: 'Líquido', en: 'Liquid' },
  grid: { es: 'Retícula', en: 'Grid' }, silk: { es: 'Seda', en: 'Silk' }, blobs: { es: 'Blobs', en: 'Blobs' },
  galaxy: { es: 'Galaxia', en: 'Galaxy' }, bokeh: { es: 'Luces', en: 'Bokeh' }, waves: { es: 'Olas', en: 'Waves' }, plain: { es: 'Liso', en: 'Plain' },
};

const STAR_LABELS: Record<ConstellationId, { es: string; en: string }> = {
  none: { es: 'Ninguna', en: 'None' }, particles: { es: 'Solo partículas', en: 'Particles only' }, virgo: { es: 'Virgo', en: 'Virgo' },
  orion: { es: 'Orión', en: 'Orion' }, ursa: { es: 'Osa Mayor', en: 'Big Dipper' }, cassiopeia: { es: 'Casiopea', en: 'Cassiopeia' },
  scorpius: { es: 'Escorpio', en: 'Scorpius' }, leo: { es: 'Leo', en: 'Leo' }, crux: { es: 'Cruz del Sur', en: 'Southern Cross' },
};

const WISHLIST_LABELS: Record<WishlistProvider, string> = {
  throne: 'Throne', wishtender: 'WishTender', amazon: 'Amazon', buymeacoffee: 'Buy Me a Coffee', kofi: 'Ko-fi', other: 'Otra',
};

/** Swatches for the brand colour: a quick way to try the creator's own colour. */
const BRAND_SWATCHES = ['#dc394b', '#ec4899', '#a855f7', '#3b82f6', '#06b6d4', '#22c55e', '#f59e0b', '#ff6a3d', '#b6ff3b', '#1f4a3a'];
const GROUND_SWATCHES = ['#020202', '#12100e', '#0b0d12', '#1e1b4b', '#fbf3ef', '#f5f2ec', '#efebe3', '#ffffff'];

const SKIN_DOTS: Record<SkinId, [string, string]> = {
  noir: ['#020202', '#dc394b'], carbon: ['#0a0c0f', '#3d8bff'], 'studio-night': ['#12100e', '#ff6a3d'], arcade: ['#0b0d12', '#b6ff3b'],
  studio: ['#f5f2ec', '#b5340e'], blush: ['#fbf3ef', '#a8325a'], atelier: ['#efebe3', '#1f4a3a'],
};

const label: React.CSSProperties = {
  fontSize: 'var(--fs-micro)', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700,
  color: 'var(--c-text-dim)', margin: '14px 0 8px',
};

const Chip: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode; style?: React.CSSProperties; title?: string }> = ({ on, onClick, children, style, title }) => (
  <button
    type="button" onClick={onClick} aria-pressed={on} title={title}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', cursor: 'pointer',
      borderRadius: 'calc(10px * var(--radius-scale, 1))',
      border: `1px solid ${on ? 'var(--c-crimson)' : 'var(--c-line)'}`,
      background: on ? 'rgb(var(--c-crimson-rgb) / 0.12)' : 'rgb(var(--c-tint) / 0.03)',
      color: on ? 'var(--c-crimson)' : 'var(--c-text)', fontFamily: 'var(--ff-body)', fontSize: 12, fontWeight: 600,
      transition: 'all .2s', ...style,
    }}
  >
    {children}
  </button>
);

const Slider: React.FC<{ value: number; min: number; max: number; step: number; onChange: (v: number) => void; left?: string; right?: string; format?: (v: number) => string }> =
  ({ value, min, max, step, onChange, left, right, format }) => (
    <div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ width: '100%', accentColor: 'var(--c-crimson)' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--c-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        <span>{left}</span><span style={{ color: 'var(--c-text)', fontWeight: 600 }}>{format ? format(value) : value}</span><span>{right}</span>
      </div>
    </div>
  );

const ColorField: React.FC<{ name: string; value: string; onChange: (hex: string) => void; swatches?: string[]; onClear?: () => void; custom: boolean }> =
  ({ name, value, onChange, swatches, onClear, custom }) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text)' }}>{name}</span>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <span style={{ fontSize: 11, color: 'var(--c-muted)', fontVariantNumeric: 'tabular-nums' }}>{value.toUpperCase()}</span>
          <span style={{ position: 'relative', width: 28, height: 28, borderRadius: 'calc(8px * var(--radius-scale, 1))', background: value, border: '1px solid var(--c-line)', boxShadow: custom ? '0 0 0 2px var(--c-crimson)' : 'none' }}>
            <input type="color" value={value} onChange={(e) => onChange(e.target.value)} aria-label={name}
              style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }} />
          </span>
          {custom && onClear && (
            <button type="button" onClick={onClear} aria-label="reset" style={{ background: 'none', border: 'none', color: 'var(--c-muted)', cursor: 'pointer', padding: 2 }}>
              <X style={{ width: 12, height: 12 }} />
            </button>
          )}
        </label>
      </div>
      {swatches && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {swatches.map((s) => (
            <button key={s} type="button" onClick={() => onChange(s)} aria-label={s}
              style={{
                width: 22, height: 22, borderRadius: 'calc(6px * var(--radius-scale, 1))', background: s, cursor: 'pointer',
                border: `1px solid ${hexIsLight(s) ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.15)'}`,
                outline: s.toLowerCase() === value.toLowerCase() ? '2px solid var(--c-crimson)' : 'none', outlineOffset: 1,
              }} />
          ))}
        </div>
      )}
    </div>
  );

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: 'var(--ff-body)', color: 'var(--c-text)',
  background: 'rgb(var(--c-tint) / 0.04)', border: '1px solid var(--c-line)', borderRadius: 'calc(10px * var(--radius-scale, 1))', outline: 'none',
};

const TextField: React.FC<{ name?: string; value: string; placeholder?: string; onChange: (v: string) => void; multiline?: boolean; inputMode?: 'numeric' | 'url' | 'text'; prefix?: string }> =
  ({ name, value, placeholder, onChange, multiline, inputMode = 'text', prefix }) => (
    <label style={{ display: 'block', marginBottom: 10 }}>
      {name && <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--c-text)', marginBottom: 6 }}>{name}</span>}
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {prefix && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--c-text-dim)', minWidth: 26, textAlign: 'center' }}>{prefix}</span>}
        {multiline
          ? <textarea value={value} placeholder={placeholder} rows={3} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
          : <input value={value} placeholder={placeholder} inputMode={inputMode} onChange={(e) => onChange(e.target.value)} style={inputStyle} autoCapitalize="off" autoCorrect="off" spellCheck={false} />}
      </span>
    </label>
  );

const Toggle: React.FC<{ on: boolean; onChange: (v: boolean) => void; label: string; hint?: string }> = ({ on, onChange, label, hint }) => (
  <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)}
    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text)', fontFamily: 'inherit', textAlign: 'left' }}>
    <span>
      <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{label}</span>
      {hint && <span style={{ display: 'block', fontSize: 11, color: 'var(--c-muted)', marginTop: 2, lineHeight: 1.4 }}>{hint}</span>}
    </span>
    <span style={{ flex: '0 0 auto', width: 40, height: 22, borderRadius: 999, background: on ? 'var(--c-crimson)' : 'rgb(var(--c-tint) / 0.15)', position: 'relative', transition: 'background .2s' }}>
      <span style={{ position: 'absolute', top: 3, left: on ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: on ? 'var(--c-on-crimson)' : 'var(--c-text)', transition: 'left .2s' }} />
    </span>
  </button>
);

/** Copies text inside Telegram's webview too (no clipboard API there on some clients). */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

const digits = (v: string) => {
  const n = parseInt(v.replace(/\D/g, ''), 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

export const DemoCustomizer: React.FC<{ lang?: 'es' | 'en' }> = ({ lang = 'es' }) => {
  if (!IS_DEMO) return null;
  return <CustomizerInner lang={lang} />;
};

const CustomizerInner: React.FC<{ lang: 'es' | 'en' }> = ({ lang }) => {
  const c = useDemoCustomization();
  const { hapticFeedback } = useTelegram();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('look');
  const t = COPY[lang];

  const { config } = useAppConfig();
  const content = c.content ?? {};
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'fail'>('idle');
  const [sendState, setSendState] = useState<'idle' | 'sending' | 'sent' | 'fail'>('idle');
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState(false);

  const set = (patch: DemoCustomization) => {
    hapticFeedback.selection();
    setCustomization(patch);
  };

  // Light intel for the sales team: which look a prospect ends up with (debounced, only inside Telegram)
  useEffect(() => {
    if (Object.keys(c).length) trackDemoDebounced('customize', { ...c });
  }, [c]);

  // Effective values for the controls (override → persona look → build default)
  const env = import.meta.env;
  const eff = withPersonaLook(c);
  const skinId = (eff.skin ?? (env.VITE_SKIN as SkinId) ?? 'noir') as SkinId;
  const skin = SKINS[skinId] ?? SKINS.noir;
  const bgId = (eff.background ?? (eff.skin ? skin.background.mode : (env.VITE_BACKGROUND as BackgroundId) || skin.background.mode)) as BackgroundId;
  const computed = typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null;
  const cssVar = (n: string, fb: string) => (computed?.getPropertyValue(n).trim() || fb);
  const brand = c.brand ?? toHex(cssVar('--c-crimson', '#dc394b'));
  const accent = c.accent ?? toHex(cssVar('--c-gold', '#c9a24a'));
  const text = c.text ?? toHex(cssVar('--c-text', '#ececec'));
  const ground = c.bg ?? toHex(cssVar('--c-bg', skin.headerColor));
  const color1 = c.color ?? (eff.skin ? brand : (env.VITE_BG_COLOR as string) || brand);
  const color2 = c.color2 ?? (eff.skin ? accent : (env.VITE_BG_COLOR2 as string) || accent);
  const radius: RadiusId = eff.radius ?? 'round';
  const dirty = isCustomized();
  const appearance = resolveAppearance(undefined, eff);
  const starsId = appearance.constellation;
  const interactive = appearance.interactive;
  const promoPlans = buildPlans(config, false);

  // Everything the team needs to build the real app, with the raw overrides for "Pegar una configuración"
  const exported = useMemo(() => JSON.stringify({
    product: 'Creator Telegrams',
    version: 1,
    created_at: new Date().toISOString(),
    persona: getPersona().id,
    appearance: {
      skin: skinId, background: bgId, constellation: starsId, interactive, motion: appearance.motion, density: appearance.density,
      colors: { brand, accent, ground, text, background_1: color1, background_2: color2 },
      radius, font_display: c.fontDisplay ?? null, font_body: c.fontBody ?? null, font_scale: c.fontScale ?? 1,
    },
    profile: { name: config.profile.name, bio: config.profile.bio },
    pricing: {
      currency: config.pricing.currency, first_day: config.pricing.vipMonthly, monthly: config.pricing.vipYearly,
      promos: promoPlans.map((p) => ({ months: p.months, price: p.price, discount: p.discount })),
    },
    social_links: config.socialLinks.filter((l) => l.enabled && l.url).map((l) => ({ platform: l.platform, url: l.url })),
    wishlist: config.wishlist,
    overrides: c,
  }, null, 2), [c, config, skinId, bgId, starsId, interactive, appearance.motion, appearance.density, brand, accent, ground, text, color1, color2, radius, promoPlans]);

  const onCopy = async () => {
    hapticFeedback.impact('light');
    const ok = await copyText(exported);
    setCopyState(ok ? 'copied' : 'fail');
    setTimeout(() => setCopyState('idle'), 2200);
  };
  const onSend = async () => {
    hapticFeedback.impact('medium');
    setSendState('sending');
    const ok = await sendDemoEvent('config_export', { config: JSON.parse(exported) });
    setSendState(ok ? 'sent' : 'fail');
    if (ok) hapticFeedback.notification('success');
  };
  const onImport = () => {
    const parsed = parseCustomization(importText);
    if (!parsed || !Object.keys(parsed).length) { setImportError(true); return; }
    hapticFeedback.notification('success');
    replaceCustomization(parsed);
    setImportText('');
    setImportError(false);
  };

  const displayFonts = FONTS.filter((f) => f.kind !== 'body');
  const bodyFonts = FONTS.filter((f) => f.kind !== 'display');

  return (
    <>
      <motion.button
        type="button"
        initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 0.4 }}
        whileTap={{ scale: 0.92 }}
        onClick={() => { hapticFeedback.impact('light'); setOpen(true); }}
        aria-label={t.open} title={t.open}
        style={{
          position: 'absolute', top: 14, right: 16, zIndex: 21,
          width: 34, height: 34, borderRadius: 'var(--radius-pill, 999px)', cursor: 'pointer',
          border: `1px solid ${dirty ? 'var(--c-crimson)' : 'var(--c-line)'}`, background: 'var(--c-surface)',
          backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
          color: dirty ? 'var(--c-crimson)' : 'var(--c-text)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Palette style={{ width: 16, height: 16 }} />
        {dirty && <span style={{ position: 'absolute', top: -2, right: -2, width: 8, height: 8, borderRadius: '50%', background: 'var(--c-crimson)' }} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="bd" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
              onClick={() => setOpen(false)}
              style={{ position: 'absolute', inset: 0, zIndex: 70, background: 'rgb(var(--c-tint) / 0.12)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)' }}
            />
            <motion.section
              key="sheet" role="dialog" aria-label={t.title}
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring' as const, stiffness: 320, damping: 32 }}
              style={{
                position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 71, maxHeight: '78%',
                display: 'flex', flexDirection: 'column',
                background: 'var(--c-sheet)', borderTop: '1px solid var(--c-line)',
                borderRadius: 'calc(20px * var(--radius-scale, 1)) calc(20px * var(--radius-scale, 1)) 0 0',
                backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                color: 'var(--c-text)', fontFamily: 'var(--ff-body)',
              }}
            >
              {/* Header */}
              <div style={{ padding: '12px 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 'var(--fs-micro)', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--c-crimson)', fontWeight: 700 }}>{t.subtitle}</div>
                  <div style={{ fontFamily: 'var(--ff-display)', fontSize: 22, letterSpacing: '0.02em', marginTop: 2 }}>{t.title}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" onClick={() => { hapticFeedback.impact('medium'); resetCustomization(); }} disabled={!dirty} aria-label={t.reset}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 10px', cursor: dirty ? 'pointer' : 'default',
                      borderRadius: 'calc(10px * var(--radius-scale, 1))', border: '1px solid var(--c-line)', background: 'transparent',
                      color: dirty ? 'var(--c-text)' : 'var(--c-muted)', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: dirty ? 1 : 0.6,
                    }}>
                    <RotateCcw style={{ width: 13, height: 13 }} /> {t.reset}
                  </button>
                  <button type="button" onClick={() => setOpen(false)} aria-label={t.done}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', cursor: 'pointer',
                      borderRadius: 'calc(10px * var(--radius-scale, 1))', border: 'none', background: 'var(--c-crimson)', color: 'var(--c-on-crimson)',
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                    }}>
                    <Check style={{ width: 13, height: 13 }} /> {t.done}
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div role="tablist" style={{ display: 'flex', gap: 4, padding: '0 12px', borderBottom: '1px solid var(--c-line)', overflowX: 'auto' }}>
                {(Object.keys(t.tabs) as Tab[]).map((id) => (
                  <button key={id} role="tab" type="button" aria-selected={tab === id} onClick={() => setTab(id)}
                    style={{
                      flex: '1 0 auto', padding: '10px 8px', background: 'none', border: 'none', cursor: 'pointer',
                      color: tab === id ? 'var(--c-crimson)' : 'var(--c-text-dim)', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
                      borderBottom: `2px solid ${tab === id ? 'var(--c-crimson)' : 'transparent'}`, marginBottom: -1, fontFamily: 'inherit',
                    }}>
                    {t.tabs[id]}
                  </button>
                ))}
              </div>

              {/* Body */}
              <div style={{ overflowY: 'auto', padding: '0 16px 16px', WebkitOverflowScrolling: 'touch' }}>
                {tab === 'look' && (
                  <>
                    <div style={label}>{t.presets}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {PRESETS.map((p) => {
                        const dots = SKIN_DOTS[p.values.skin ?? 'noir'];
                        const on = Object.entries(p.values).every(([k, v]) => (c as Record<string, unknown>)[k] === v);
                        return (
                          <button key={p.id} type="button" onClick={() => { hapticFeedback.impact('medium'); resetLook(); setCustomization(p.values); }}
                            style={{
                              textAlign: 'left', padding: 10, cursor: 'pointer', borderRadius: 'calc(12px * var(--radius-scale, 1))',
                              border: `1px solid ${on ? 'var(--c-crimson)' : 'var(--c-line)'}`, background: 'rgb(var(--c-tint) / 0.03)', color: 'var(--c-text)', fontFamily: 'inherit',
                            }}>
                            <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                              <span style={{ width: 18, height: 18, borderRadius: 'calc(5px * var(--radius-scale, 1))', background: dots[0], border: '1px solid var(--c-line)' }} />
                              <span style={{ width: 18, height: 18, borderRadius: 'calc(5px * var(--radius-scale, 1))', background: p.values.brand ?? dots[1] }} />
                              {p.values.accent && <span style={{ width: 18, height: 18, borderRadius: 'calc(5px * var(--radius-scale, 1))', background: p.values.accent }} />}
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 700 }}>{p.label}</div>
                            <div style={{ fontSize: 11, color: 'var(--c-muted)', marginTop: 2 }}>{p.hint}</div>
                          </button>
                        );
                      })}
                    </div>

                    <div style={label}>{t.skins}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {(Object.keys(SKINS) as SkinId[]).map((id) => (
                        <Chip key={id} on={skinId === id} onClick={() => set({ skin: id, background: undefined, color: undefined, color2: undefined, bg: undefined, text: undefined })}>
                          <span style={{ width: 12, height: 12, borderRadius: '50%', background: `linear-gradient(135deg, ${SKIN_DOTS[id][0]} 50%, ${SKIN_DOTS[id][1]} 50%)`, border: '1px solid var(--c-line)' }} />
                          {SKINS[id].name}
                        </Chip>
                      ))}
                    </div>
                  </>
                )}

                {tab === 'bg' && (
                  <>
                    <div style={label}>{t.bgs}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {BACKGROUNDS.map((id) => (
                        <Chip key={id} on={bgId === id} onClick={() => set({ background: id })}>{BG_LABELS[id][lang]}</Chip>
                      ))}
                    </div>
                    <div style={label}>{t.stars}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {CONSTELLATIONS.map((id) => (
                        <Chip key={id} on={starsId === id} onClick={() => set({ constellation: id })}>{STAR_LABELS[id][lang]}</Chip>
                      ))}
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <Toggle on={interactive} onChange={(v) => set({ interactive: v })} label={t.interactive} hint={t.interactiveHint} />
                    </div>
                    <div style={label}>{t.bgColors}</div>
                    <ColorField name="Color 1" value={color1} custom={!!c.color} onChange={(v) => set({ color: v })} onClear={() => set({ color: undefined })} />
                    <ColorField name="Color 2" value={color2} custom={!!c.color2} onChange={(v) => set({ color2: v })} onClear={() => set({ color2: undefined })} />
                    <div style={label}>{t.motion}</div>
                    <Slider value={c.motion ?? Number(env.VITE_BG_MOTION ?? 1)} min={0} max={2} step={0.1} onChange={(v) => set({ motion: v })} left={t.still} right={t.fast} format={(v) => `${v.toFixed(1)}×`} />
                    <div style={label}>{t.density}</div>
                    <Slider value={c.density ?? Number(env.VITE_BG_DENSITY ?? 1)} min={0.25} max={2} step={0.05} onChange={(v) => set({ density: v })} left="−" right="+" format={(v) => `${v.toFixed(2)}×`} />
                  </>
                )}

                {tab === 'color' && (
                  <>
                    <div style={label}>{t.palettes}</div>
                    <ColorField name={t.brand} value={brand} custom={!!c.brand} swatches={BRAND_SWATCHES} onChange={(v) => set({ brand: v })} onClear={() => set({ brand: undefined })} />
                    <ColorField name={t.accent} value={accent} custom={!!c.accent} onChange={(v) => set({ accent: v })} onClear={() => set({ accent: undefined })} />
                    <ColorField name={t.ground} value={ground} custom={!!c.bg} swatches={GROUND_SWATCHES} onChange={(v) => set({ bg: v })} onClear={() => set({ bg: undefined, text: c.text })} />
                    <ColorField name={t.text} value={text} custom={!!c.text} onChange={(v) => set({ text: v })} onClear={() => set({ text: undefined })} />
                  </>
                )}

                {tab === 'shape' && (
                  <>
                    <div style={label}>{t.radius}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                      {(Object.keys(RADIUS_SCALE) as RadiusId[]).map((id) => {
                        const r = 14 * RADIUS_SCALE[id];
                        const on = radius === id;
                        return (
                          <button key={id} type="button" onClick={() => set({ radius: id })} aria-pressed={on}
                            style={{
                              padding: '12px 6px', cursor: 'pointer', background: on ? 'rgb(var(--c-crimson-rgb) / 0.12)' : 'rgb(var(--c-tint) / 0.03)',
                              border: `1px solid ${on ? 'var(--c-crimson)' : 'var(--c-line)'}`, borderRadius: 'calc(12px * var(--radius-scale, 1))', color: 'var(--c-text)', fontFamily: 'inherit',
                            }}>
                            <div style={{ width: 40, height: 28, margin: '0 auto 8px', borderRadius: id === 'pill' ? 999 : r, border: `2px solid ${on ? 'var(--c-crimson)' : 'var(--c-text-dim)'}`, background: on ? 'rgb(var(--c-crimson-rgb) / 0.2)' : 'transparent' }} />
                            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t.radiusNames[id]}</div>
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: 16, padding: 12, border: '1px solid var(--c-line)', borderRadius: 'calc(12px * var(--radius-scale, 1))', background: 'var(--c-surface)' }}>
                      <div style={{ fontFamily: 'var(--ff-display)', fontSize: 18, marginBottom: 6 }}>VIP · 30.000 COP</div>
                      <div style={{ fontSize: 12, color: 'var(--c-text-dim)', marginBottom: 10 }}>{lang === 'es' ? 'Así se ven las tarjetas y botones.' : 'This is how cards and buttons look.'}</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <span style={{ padding: '8px 14px', background: 'var(--c-crimson)', color: 'var(--c-on-crimson)', borderRadius: 'var(--radius-pill, 999px)', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{lang === 'es' ? 'Unirme' : 'Join'}</span>
                        <span style={{ padding: '8px 14px', border: '1px solid var(--c-line)', borderRadius: 'calc(10px * var(--radius-scale, 1))', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{lang === 'es' ? 'Ver más' : 'See more'}</span>
                      </div>
                    </div>
                  </>
                )}

                {tab === 'type' && (
                  <>
                    <div style={label}>{t.display}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      {displayFonts.map((f) => (
                        <Chip key={f.id} on={(c.fontDisplay ?? '') === f.id} onClick={() => set({ fontDisplay: f.id as FontId })} style={{ justifyContent: 'space-between' }}>
                          <span style={{ fontFamily: f.family, fontSize: 15, fontWeight: 700, letterSpacing: 0, textTransform: 'none' }}>{f.label}</span>
                        </Chip>
                      ))}
                    </div>
                    <div style={label}>{t.body}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      {bodyFonts.map((f) => (
                        <Chip key={f.id} on={(c.fontBody ?? '') === f.id} onClick={() => set({ fontBody: f.id as FontId })}>
                          <span style={{ fontFamily: f.family, fontSize: 13, fontWeight: 500 }}>{f.label}</span>
                        </Chip>
                      ))}
                    </div>
                    <div style={label}>{t.size}</div>
                    <Slider value={c.fontScale ?? 1} min={0.8} max={1.25} step={0.05} onChange={(v) => set({ fontScale: v })} left="A" right="A" format={(v) => `${Math.round(v * 100)}%`} />
                  </>
                )}

                {tab === 'profile' && (
                  <>
                    <div style={label}>{t.name}</div>
                    <TextField value={content.name ?? ''} placeholder={config.profile.name} onChange={(v) => setContent({ name: v || undefined })} />
                    <div style={label}>{t.bio}</div>
                    <TextField multiline value={content.bio ?? ''} placeholder={config.profile.bio} onChange={(v) => setContent({ bio: v || undefined })} />
                    <div style={label}>{t.prices}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <TextField name={t.priceFirst} inputMode="numeric" value={content.priceFirst ? content.priceFirst.toLocaleString('es-CO') : ''} placeholder={config.pricing.vipMonthly.toLocaleString('es-CO')} onChange={(v) => setContent({ priceFirst: digits(v) })} />
                      <TextField name={t.priceMonthly} inputMode="numeric" value={content.priceMonthly ? content.priceMonthly.toLocaleString('es-CO') : ''} placeholder={config.pricing.vipYearly.toLocaleString('es-CO')} onChange={(v) => setContent({ priceMonthly: digits(v) })} />
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--c-muted)', lineHeight: 1.5, margin: '2px 0 8px' }}>{t.plansHint}</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                      {promoPlans.map((p) => (
                        <div key={p.id} style={{ padding: '8px 10px', border: '1px solid var(--c-line)', borderRadius: 'calc(10px * var(--radius-scale, 1))', fontSize: 12 }}>
                          <div style={{ fontWeight: 700 }}>{planLabel(p, lang)}{p.discount ? ` · −${Math.round(p.discount * 100)}%` : ''}</div>
                          <div style={{ color: 'var(--c-text-dim)', marginTop: 2 }}>${p.price.toLocaleString('es-CO')}</div>
                        </div>
                      ))}
                    </div>
                    <div style={label}>{t.plansView}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {([[undefined, t.planAuto], ['first', t.planFirst], ['later', t.planLater]] as const).map(([v, name]) => (
                        <Chip key={name} on={content.pricingView === v} onClick={() => { hapticFeedback.selection(); setContent({ pricingView: v }); }}>{name}</Chip>
                      ))}
                    </div>
                  </>
                )}

                {tab === 'socials' && (
                  <>
                    <div style={label}>{t.networks}</div>
                    <p style={{ fontSize: 11, color: 'var(--c-muted)', lineHeight: 1.5, margin: '0 0 10px' }}>{t.networksHint}</p>
                    {SOCIAL_PLATFORMS.map((p) => {
                      const demoLink = config.socialLinks.find((l) => l.id === p.id && l.enabled && l.url);
                      return (
                        <TextField key={p.id} prefix={p.tag} value={content.socials?.[p.id] ?? ''}
                          placeholder={demoLink ? socialHandle(p.id, demoLink.url) : p.id === 'web' ? 'tusitio.com' : `${p.name} · @usuario`}
                          inputMode={p.id === 'web' ? 'url' : 'text'}
                          onChange={(v) => setContent({ socials: { ...content.socials, [p.id]: v } })} />
                      );
                    })}
                    <div style={label}>{t.wishlist}</div>
                    <Toggle on={config.wishlist.enabled} onChange={(v) => setContent({ wishlist: { ...content.wishlist, enabled: v } })} label={t.wishlistOn} />
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '6px 0 10px' }}>
                      {WISHLIST_PROVIDERS.map((id) => (
                        <Chip key={id} on={config.wishlist.provider === id} onClick={() => { hapticFeedback.selection(); setContent({ wishlist: { ...content.wishlist, provider: id, enabled: true } }); }}>{WISHLIST_LABELS[id]}</Chip>
                      ))}
                    </div>
                    <TextField name={t.wishlistUrl} inputMode="url" value={content.wishlist?.url ?? ''} placeholder={config.wishlist.url || 'throne.com/tuusuario'}
                      onChange={(v) => setContent({ wishlist: { ...content.wishlist, url: v || undefined } })} />
                  </>
                )}

                {tab === 'export' && (
                  <>
                    <div style={label}>{t.exportTitle}</div>
                    <p style={{ fontSize: 12, color: 'var(--c-text-dim)', lineHeight: 1.5, margin: '0 0 10px' }}>{t.exportHint}</p>
                    <textarea readOnly value={exported} rows={9} onFocus={(e) => e.currentTarget.select()}
                      style={{ ...inputStyle, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11, lineHeight: 1.4, resize: 'vertical' }} />
                    <div style={{ display: 'grid', gridTemplateColumns: canSendToTeam() ? '1fr 1fr' : '1fr', gap: 8, marginTop: 10 }}>
                      <button type="button" onClick={onCopy}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px', cursor: 'pointer', border: 'none', borderRadius: 'calc(10px * var(--radius-scale, 1))', background: 'var(--c-crimson)', color: 'var(--c-on-crimson)', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'inherit' }}>
                        {copyState === 'copied' ? <Check style={{ width: 14, height: 14 }} /> : <Copy style={{ width: 14, height: 14 }} />}
                        {copyState === 'copied' ? t.copied : t.copy}
                      </button>
                      {canSendToTeam() && (
                        <button type="button" onClick={onSend} disabled={sendState === 'sending' || sendState === 'sent'}
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px', cursor: 'pointer', border: '1px solid var(--c-crimson)', borderRadius: 'calc(10px * var(--radius-scale, 1))', background: 'transparent', color: 'var(--c-crimson)', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'inherit', opacity: sendState === 'sending' ? 0.6 : 1 }}>
                          {sendState === 'sent' ? <Check style={{ width: 14, height: 14 }} /> : <Send style={{ width: 14, height: 14 }} />}
                          {sendState === 'sent' ? t.sent : t.send}
                        </button>
                      )}
                    </div>
                    {(copyState === 'fail' || sendState === 'fail') && (
                      <p style={{ fontSize: 11, color: 'var(--c-crimson)', marginTop: 8 }}>{sendState === 'fail' ? t.sendFail : lang === 'es' ? 'Selecciona el texto y cópialo a mano.' : 'Select the text and copy it by hand.'}</p>
                    )}
                    <div style={label}>{t.importTitle}</div>
                    <textarea value={importText} rows={3} placeholder="{ … }" onChange={(e) => { setImportText(e.target.value); setImportError(false); }}
                      style={{ ...inputStyle, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11, resize: 'vertical' }} />
                    {importError && <p style={{ fontSize: 11, color: 'var(--c-crimson)', marginTop: 6 }}>{t.importBad}</p>}
                    <button type="button" onClick={onImport} disabled={!importText.trim()}
                      style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 12px', cursor: importText.trim() ? 'pointer' : 'default', border: '1px solid var(--c-line)', borderRadius: 'calc(10px * var(--radius-scale, 1))', background: 'transparent', color: 'var(--c-text)', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'inherit', opacity: importText.trim() ? 1 : 0.5 }}>
                      <Upload style={{ width: 13, height: 13 }} /> {t.importBtn}
                    </button>
                  </>
                )}

                <p style={{ marginTop: 18, fontSize: 12, lineHeight: 1.5, color: 'var(--c-text-dim)', fontWeight: 600 }}>{t.note}</p>
              </div>
            </motion.section>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

/** rgb(a, b, c) or #hex → #hex (colour inputs only accept 6-digit hex). */
function toHex(v: string): string {
  const s = v.trim();
  if (/^#[0-9a-f]{6}$/i.test(s)) return s.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(s)) return ('#' + s.slice(1).split('').map((ch) => ch + ch).join('')).toLowerCase();
  const m = s.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
  if (m) return '#' + [m[1], m[2], m[3]].map((n) => parseInt(n, 10).toString(16).padStart(2, '0')).join('');
  return '#dc394b';
}

export default DemoCustomizer;
